/* eslint-disable react/jsx-no-bind */
/**
 * @file 侧边导航用户信息
 * @author sunyingying01
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {App, Popover, Tooltip} from 'antd';
import {useNavigate} from 'react-router-dom';
import {flowResult} from 'mobx';
import {isNil} from 'lodash';

import CopyOutlined from '@assets/icons/copy.svg';
import RefreshSvg from '@/assets/icons/refreshApiKey.svg';
import NextArrowSvg from '@/assets/icons/next1.svg';
import PersonalInfoSvg from '@/assets/icons/personalInfo.svg';
import GuideSvg from '@/assets/icons/question.svg';
import DocSvg from '@/assets/icons/doc.svg';
import ContactSvg from '@/assets/icons/connect.svg';
import FamouSvg from '@/assets/icons/famou.svg';
import OpenSvg from '@/assets/billing/open.svg';
import LogoutSvg from '@/assets/icons/logout.svg';
import {useRequestErrorHandler} from '@/utils/errorHandler';
import {CopyButton} from '@/components/commonUi';
import {useConfirmModal} from '@/components/ConfirmModal';
import GuideModal from '@/components/GuideModal';
import {getSSOHost} from '@/api/login';
import {formatNumberWithCommas} from '@/utils';
import {IProps} from '../Menus';
import styles from './index.module.less';

interface UserInfoCardProps {
    store: IProps['store'];
    onClose?: () => void;
}

type ProfileMenuKey = 'profile' | 'guide' | 'doc' | 'contact' | 'agent' | 'enterpriseAdmin';

interface IProfileMenuItem {
    key: ProfileMenuKey;
    name: string;
    icon: React.ReactNode;
    url: string;
}

const menuItems: IProfileMenuItem[] = [
    {key: 'profile', name: '个人信息', icon: <PersonalInfoSvg />, url: ''},
    {key: 'guide', name: '新手引导', icon: <GuideSvg />, url: ''},
    {key: 'doc', name: '使用文档', icon: <DocSvg />, url: 'https://cloud.baidu.com/doc/famou/index.html'},
    {key: 'contact', name: '联系我们', icon: <ContactSvg />, url: 'https://cloud.baidu.com/survey/famou-from.html'},
    {key: 'agent', name: '百度伐谋·产业决策智能体', icon: <FamouSvg />, url: ''},
    {key: 'enterpriseAdmin', name: '企业管理平台', icon: <PersonalInfoSvg />, url: '/admin'}
];

const UserInfoCard: React.FC<UserInfoCardProps> = props => {
    const {message} = App.useApp();
    const navigate = useNavigate();
    const {rootStore, projectsListStore, taskRunningStore} = props.store;
    const userStore = rootStore.userStore;
    const {jobsListStore} = projectsListStore;
    const {handleError} = useRequestErrorHandler();
    const {showConfirm} = useConfirmModal();

    const [activeMenu, setActiveMenu] = useState<ProfileMenuKey | ''>('');
    const [profilePopoverOpen, setProfilePopoverOpen] = useState(false);

    const handleNavigateToBilling = useCallback(
        () => {
            if (taskRunningStore?.confirmDialogStore?.showJobConfigPage) {
                taskRunningStore.confirmDialogStore.setShowJobConfigPage(false);
            }
            props.onClose?.();
            navigate('/billing');
        },
        [navigate, props]
    );

    const planName = userStore.enterprisePlanText;
    const dailyCreditText = useMemo(
        () => (isNil(userStore.dailyCredit) ? '—' : formatNumberWithCommas(userStore.dailyCredit)),
        [userStore.dailyCredit]
    );

    useEffect(
        () => {
            void flowResult(userStore.fetchAccountInfo());
        },
        [userStore]
    );

    const handleLogout = useCallback(
        () => {
            flowResult(userStore.logout());
        },
        [userStore]
    );

    const onCopy = useCallback(
        () => {
            message.success('复制成功');
        },
        [message]
    );

    const handleRefresh = useCallback(
        async () => {
            const refreshVisibleJobs = jobsListStore.refreshVisibleJobs;
            if (refreshVisibleJobs.length === 0) {
                try {
                    await flowResult(userStore.refreshApikeyInfo());
                    message.success('API Key 更新成功');
                } catch (error) {
                    handleError(error);
                }
                return;
            }
            showConfirm(
                {
                    title: '更新 API Key',
                    content: (
                        <div>
                            <div className={styles.apiKeyNotice}>
                                <span className={styles.apiKeyNoticeHighlight}>注意：</span>
                                刷新后，以下任务将终止且无法恢复，建议等待任务完成后再刷新。
                            </div>
                            <div className={styles.jobProgressCard}>
                                <div className={`${styles.jobProgressItem} ${styles.jobProgressHeader}`}>
                                    <span className={styles.jobName}>任务名称</span>
                                    <span className={styles.jobProgressValue}>进度</span>
                                </div>
                                <div className={styles.jobProgressList}>
                                    {refreshVisibleJobs.map(job => {
                                        const progress = job.iterationSum > 0
                                            ? ((job.iteration / job.iterationSum) * 100).toFixed(1)
                                            : '0.0';
                                        return (
                                            <div key={job.job_id} className={styles.jobProgressItem}>
                                                <span className={styles.jobName}>{job.name}</span>
                                                <span className={styles.jobProgressHighlight}>{progress}%</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ),
                    okText: '确认更新',
                    successMessage: 'API Key 更新成功',
                    danger: false,
                    footerExtra: null
                },
                async () => {
                    try {
                        await flowResult(userStore.refreshApikeyInfo());
                    }
                    catch (error) {
                        handleError(error);
                    }
                }
            );
        },
        [handleError, userStore, jobsListStore, showConfirm, message]
    );

    const profileContent = useMemo(
        () => (
            <section className={styles.detailCard}>
                <div className={styles.detailLine}>
                    <span className={styles.detailLabel}>ID:</span>
                    <Tooltip title={userStore.userId}>
                        <span className={styles.detailValue}>{userStore.userId || '暂无'}</span>
                    </Tooltip>
                    <CopyButton
                        copyIcon={<span className={styles.copyIcon}><CopyOutlined /></span>}
                        onCopyCallbacks={onCopy}
                        text={userStore.userId}
                    />
                </div>

                <div className={styles.detailLine}>
                    <span className={styles.detailLabel}>API Key:</span>
                    <Tooltip title={userStore.apiKey}>
                        <span className={styles.detailValue}>{userStore.apiKey || '暂无'}</span>
                    </Tooltip>
                    <div className={styles.apiActionGroup}>
                        <Tooltip title="更新">
                            <span className={styles.refreshApiKey} onClick={handleRefresh}><RefreshSvg /></span>
                        </Tooltip>
                        <CopyButton
                            copyIcon={<span className={styles.copyIcon}><CopyOutlined /></span>}
                            onCopyCallbacks={onCopy}
                            text={userStore.apiKey}
                        />
                    </div>
                </div>
            </section>
        ),
        [handleRefresh, onCopy, userStore.apiKey, userStore.userId]
    );

    const handleCloseGuideModal = useCallback(
        () => {
            userStore.setShowGuideModal(false);
        },
        [userStore]
    );

    const handleMenuClick = useCallback(
        (item: IProfileMenuItem) => {
            if (item.key === 'profile') {
                setActiveMenu(item.key);
                setProfilePopoverOpen(true);
                return;
            }

            setProfilePopoverOpen(false);
            setActiveMenu('');

            if (item.key === 'agent') {
                const agentUrl = getSSOHost();
                window.open(agentUrl, '_blank');
                return;
            }

            if (item.url) {
                window.open(item.url, '_blank');
                return;
            }

            userStore.setShowGuideModal(true);
        },
        [userStore]
    );

    return (
        <section className={styles.userCard}>
            <header className={styles.cardHeader} onClick={handleNavigateToBilling}>
                <div className={styles.planRow}>
                    <span className={styles.planText}>{planName}</span>
                    <button
                        type="button"
                        className={styles.upgradeBtn}
                    >
                        升级订阅
                        <span className={styles.arrowIcon}><NextArrowSvg /></span>
                    </button>
                </div>
                <div className={styles.pointsRow}>
                    <span className={styles.pointsValue}>{dailyCreditText}</span>
                    <span className={styles.pointsLabel}>可用资源点</span>
                </div>
            </header>

            <nav className={styles.menuNav}>
                {menuItems.filter(item => {
                    if (item.key === 'enterpriseAdmin') {
                        return userStore.isEnterpriseAdmin;
                    }
                    return true;
                }).map(item => {
                    const isActive = item.key === activeMenu;
                    const menuButton = (
                        <button
                            key={item.key}
                            type="button"
                            className={`${styles.menuBtn} ${isActive ? styles.menuBtnActive : ''}`}
                            onClick={() => handleMenuClick(item)}
                        >
                            <span className={styles.menuBtnLeft}>
                                <span className={styles.menuIcon}>{item.icon}</span>
                                <span>{item.name}</span>
                            </span>
                            <span className={`${(item.url || item.key === 'agent')
                                ? styles.externalIcon : styles.menuArrow}`}
                            >
                                {(item.url || item.key === 'agent') ? <OpenSvg /> : <NextArrowSvg />}
                            </span>
                        </button>
                    );

                    if (item.key !== 'profile') {
                        return menuButton;
                    }

                    return (
                        <Popover
                            key={item.key}
                            trigger="click"
                            arrow={false}
                            open={profilePopoverOpen}
                            content={profileContent}
                            placement="rightTop"
                            classNames={{root: styles.profilePopover}}
                            onOpenChange={(open: boolean) => {
                                setProfilePopoverOpen(open);
                                if (!open) {
                                    setActiveMenu('');
                                }
                            }}
                        >
                            {menuButton}
                        </Popover>
                    );
                })}
            </nav>

            <div className={styles.logoutWrap}>
                <button type="button" className={styles.logoutBtn} onClick={handleLogout}>
                    <span className={styles.logoutIcon}><LogoutSvg /></span>
                    <span>退出登录</span>
                </button>
            </div>

            <GuideModal visible={userStore.isShowGuideModal} onClose={handleCloseGuideModal} />
        </section>
    );
};

export default observer(UserInfoCard);
